import type { MessageDescriptor } from "@lingui/core";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { Activity, ClipboardList, Gauge, Scale, Waves } from "lucide-react";
import { ReactNode } from "react";

import { MeasurementRequiredData_Output } from "../api";
import { Tooltip } from "../components/ui-kit/base/tooltip";

export type DeviceConfigFilterValue =
  | "bloodPressure"
  | "weight"
  | "ppg"
  | "ecg"
  | "symptoms";

export const deviceConfigFilterOptions = new Map<
  DeviceConfigFilterValue,
  MessageDescriptor
>([
  ["bloodPressure", msg`Blood Pressure`],
  ["weight", msg`Weight`],
  ["ppg", msg`PPG`],
  ["ecg", msg`ECG`],
  ["symptoms", msg`Symptoms`],
]);

export const deviceConfigFilterIcons: Record<
  DeviceConfigFilterValue,
  ReactNode
> = {
  bloodPressure: <Gauge size={16} />,
  weight: <Scale size={16} />,
  ppg: <Waves size={16} />,
  ecg: <Activity size={16} />,
  symptoms: <ClipboardList size={16} />,
};

export function renderDeviceConfigOption(
  value: DeviceConfigFilterValue,
  label: string,
): ReactNode {
  return (
    <span className="flex items-center gap-2">
      {deviceConfigFilterIcons[value]}
      {label}
    </span>
  );
}

export function deviceConfigPredicate<
  T extends {
    self_measurement_config?: MeasurementRequiredData_Output[] | null;
  },
>(item: T, selected: unknown[]): boolean {
  return getDeviceConfigFilterValues(item.self_measurement_config).some((v) =>
    selected.includes(v),
  );
}

export function DeviceConfigIcons({
  config,
  align = "center",
}: {
  config?: MeasurementRequiredData_Output[] | null;
  align?: "left" | "center";
}) {
  const { t } = useLingui();
  if (!config?.length) return null;

  const values = getDeviceConfigFilterValues(config);
  const hasBP = values.includes("bloodPressure");
  const hasWeight = values.includes("weight");
  const hasPPG = values.includes("ppg");
  const hasECG = values.includes("ecg");
  const symptomNames = config
    .filter(
      (
        c,
      ): c is Extract<
        MeasurementRequiredData_Output,
        { data_type: "symptom" }
      > => c.data_type === "symptom",
    )
    .map((c) => c.data_name);

  return (
    <span
      className={`flex gap-1 items-center ${
        align === "left" ? "justify-start" : "justify-center"
      }`}
    >
      {hasBP && (
        <Tooltip content={t`Blood Pressure`}>
          <Gauge size={16} />
        </Tooltip>
      )}
      {hasWeight && (
        <Tooltip content={t`Weight`}>
          <Scale size={16} />
        </Tooltip>
      )}
      {hasPPG && (
        <Tooltip content={t`PPG (LVFP, HR, SpO2)`}>
          <Waves size={16} />
        </Tooltip>
      )}
      {hasECG && (
        <Tooltip content={t`ECG`}>
          <Activity size={16} />
        </Tooltip>
      )}
      {symptomNames.length > 0 && (
        <Tooltip
          content={
            <>
              <div className="font-medium mb-1">
                <Trans>Symptoms</Trans>
              </div>
              {symptomNames.map((name) => (
                <div className="capitalize" key={name}>
                  {name}
                </div>
              ))}
            </>
          }
        >
          <ClipboardList size={16} />
        </Tooltip>
      )}
    </span>
  );
}

export function getDeviceConfigFilterValues(
  config?: MeasurementRequiredData_Output[] | null,
): DeviceConfigFilterValue[] {
  if (!config?.length) return [];

  const values: DeviceConfigFilterValue[] = [];

  if (
    config.some(
      (c) =>
        c.data_type === "medical-data" &&
        c.data_name?.toLowerCase().includes("blood pressure"),
    )
  ) {
    values.push("bloodPressure");
  }

  if (
    config.some(
      (c) =>
        c.data_type === "medical-data" &&
        ["weight", "dry weight"].includes(c.data_name ?? ""),
    )
  ) {
    values.push("weight");
  }

  if (config.some((c) => c.data_type === "ppg")) {
    values.push("ppg");
  }

  if (config.some((c) => c.data_type === "ecg")) {
    values.push("ecg");
  }

  if (config.some((c) => c.data_type === "symptom")) {
    values.push("symptoms");
  }

  return values;
}
